/**
 * This file is a generic fetch client that does not know about React or store life cycles.
 * It should be treated as simply a convenience wrapper around the generated API client.
 */

import env from '~/env'

import { generatedApiClient } from './api-client.generated'
import * as apiGuards from './api-guards'
import { components, operations, paths } from './api-types.generated'
import { createBaseClient } from './base-client'
import { getPlanningDomainHeaders } from './planningDomainHeaders'
import { apiRefreshingMiddleware } from './unauthorizedHandlingMiddleware'

export const baseClient = createBaseClient<paths>({ baseUrl: env.VITE_API_BASE_URL, getRequestHeaders: getPlanningDomainHeaders })
baseClient.use(apiRefreshingMiddleware)

export type Schemas = components['schemas']

/**
 * The client is a wrapper around .GET, .POST and so on methods that help bind the path and query parameters to the response objects.
 * This allows us to use to instantiate generic "import*" methods in the store.
 *
 * Most methods are auto-generated from the OpenAPI spec (see api-client.generated.ts).
 * Only custom endpoints and special cases are manually defined here.
 */
const client = {
    ...apiGuards,
    ...generatedApiClient,

    // Custom endpoints that are not standard entity endpoints
    createSupportTicket: async (data: Schemas['SupportTicketRequest']) => await baseClient.POST('/user_support/ticket', { body: data }),

    getSupportHubSpotContact: async () => await baseClient.GET('/user_support/contact'),

    getHolidaysPerYear: async (year: number) => await baseClient.GET('/holidays/{year}', { params: { path: { year } } }),
    analyzePlanningNote: async (data: Schemas['PlanningNoteAnalysisRequest']) => await baseClient.POST('/planning-note-analysis/analyze', { body: data }),
    createHeroNoteEditIntent: async (data: Schemas['HeroNoteEditCommandRequest']) => await baseClient.POST('/planning-notes/edits/intents', { body: data }),
    reportHeroNoteEditOutcome: async (editId: string, data: Schemas['HeroNoteEditOutcomeRequest']) =>
        await baseClient.POST('/planning-notes/edits/{edit_id}/outcome', { body: data, params: { path: { edit_id: editId } } }),

    fetchPractitionerScheduleLocations: async (query: operations['getPractitionerScheduleLocations']['parameters']['query']) =>
        await baseClient.GET('/practitioner_schedule_locations/', { params: { query } }),
    fetchPractitionerScheduleStatuses: async (query: operations['getPractitionerScheduleStatuses']['parameters']['query']) =>
        await baseClient.GET('/practitioner_schedule_statuses/', { params: { query } }),

    fetchUsers: async (query: operations['getUsers']['parameters']['query']) => await baseClient.GET('/users/', { params: { query } }),

    validateStaffingSurgeryGroupsForPlanningPeriod: async (planningPeriodUuid: string) =>
        await baseClient.GET('/staffing_surgery_groups/validate/periods/{planning_period_uuid}', {
            params: { path: { planning_period_uuid: planningPeriodUuid } },
        }),

    createCommentsThread: async (data: Schemas['CreateCommentsThreadRequest']) => await baseClient.POST('/comments_threads/', { body: data }),
    editCommentsThread: async (threadId: number, data: Schemas['EditCommentsThreadRequest']) =>
        await baseClient.POST('/comments_threads/{thread_id}:edit', { params: { path: { thread_id: threadId } }, body: data }),
    archiveCommentsThread: async (threadId: number) =>
        await baseClient.POST('/comments_threads/{thread_id}:archive', { params: { path: { thread_id: threadId } } }),
    unarchiveCommentsThread: async (threadId: number) =>
        await baseClient.POST('/comments_threads/{thread_id}:unarchive', { params: { path: { thread_id: threadId } } }),
    createCommentsThreadMessage: async (threadId: number, data: Schemas['CommentsThreadCommentCreateRequest']) =>
        await baseClient.POST('/comments_threads/{thread_id}:comment', { params: { path: { thread_id: threadId } }, body: data }),
    editCommentsThreadMessage: async (messageId: number, data: Schemas['EditCommentsMessageRequest']) =>
        await baseClient.POST('/comments_messages/{message_id}:edit', { params: { path: { message_id: messageId } }, body: data }),
    createCommentsThreadReaction: async (threadId: number, data: Schemas['CommentsThreadReactRequest']) =>
        await baseClient.POST('/comments_threads/{thread_id}/reaction', { params: { path: { thread_id: threadId } }, body: data }),
    createCommentsThreadMessageReaction: async (messageId: number, data: Schemas['CommentsMessageReactRequest']) =>
        await baseClient.POST('/comments_messages/{message_id}/reaction', { params: { path: { message_id: messageId } }, body: data }),

    deleteCommentsThreadReaction: async ({ threadId, reactionId }: { threadId: number; reactionId: number }) =>
        await baseClient.DELETE('/comments_threads/{thread_id}/reaction/{reaction_id}', { params: { path: { thread_id: threadId, reaction_id: reactionId } } }),
    deleteCommentsThreadMessageReaction: async ({ messageId, reactionId }: { messageId: number; reactionId: number }) =>
        await baseClient.DELETE('/comments_messages/{message_id}/reaction/{reaction_id}', {
            params: { path: { message_id: messageId, reaction_id: reactionId } },
        }),

    resolveCommentsThread: async (threadId: number) =>
        await baseClient.POST('/comments_threads/{thread_id}:resolve', { params: { path: { thread_id: threadId } } }),
    unresolveCommentsThread: async (threadId: number) =>
        await baseClient.POST('/comments_threads/{thread_id}:unresolve', { params: { path: { thread_id: threadId } } }),
    pinCommentsThread: async (threadId: number) => await baseClient.POST('/comments_threads/{thread_id}:pin', { params: { path: { thread_id: threadId } } }),
    unpinCommentsThread: async (threadId: number) =>
        await baseClient.POST('/comments_threads/{thread_id}:unpin', { params: { path: { thread_id: threadId } } }),

    // Custom recurring assignment actions
    activateRecurringAssignment: async (id: number | string) => await baseClient.POST('/recurring_assignments/{id}/activate', { params: { path: { id } } }),
    archiveRecurringAssignment: async (id: number | string) => await baseClient.POST('/recurring_assignments/{id}/archive', { params: { path: { id } } }),
    rejectRecurringAssignment: async (id: number | string) => await baseClient.POST('/recurring_assignments/{id}/reject', { params: { path: { id } } }),

    // custom inbox actions
    markInboxEntryAsRead: async (inboxEntryId: number) =>
        await baseClient.POST('/inbox_entries/{inbox_entry_id}:read', { params: { path: { inbox_entry_id: inboxEntryId } } }),
    markAllInboxEntriesAsRead: async () => await baseClient.POST('/inbox_entries:read_all'),
    getUnreadInboxEntriesCount: async () => await baseClient.GET('/inbox_entries/unread_count'),

    // Inconsistent MailingList methods
    deleteEmailFromMailingList: async (id: number) => await baseClient.DELETE('/mailing_lists/{id}', { params: { path: { id } } }),

    // DELETE Operations
    deletePostOps: async (id: number) => await baseClient.DELETE('/post_ops/{id}', { params: { path: { id } } }),
    deletePostOpCapacityPeriods: async (id: number) => await baseClient.DELETE('/post_op_capacity_periods/{id}', { params: { path: { id } } }),
    deletePostOpCapacityOverrides: async (id: number) => await baseClient.DELETE('/post_op_capacity_overrides/{id}', { params: { path: { id } } }),
    deleteLocationPostOpAssociations: async (id: number) => await baseClient.DELETE('/location_post_op_associations/{id}', { params: { path: { id } } }),

    // Planning Note History Records Operations
    createPlanningNoteHistoryRecordForFolderId: async (folderId: string, data: Schemas['PlanningNotePostRequest']) =>
        await baseClient.POST('/planning_note_history_records/{folder_id}/planningNote', { body: data, params: { path: { folder_id: folderId } } }),
    fetchPlanningNoteHistoryRecordsByFolderId: async (folderId: string, signal: AbortSignal) =>
        await baseClient.GET('/planning_note_history_records/{folder_id}/planningNote', { params: { path: { folder_id: folderId } }, signal }),
    getAllPlanningNotes: async () => await baseClient.GET('/planning_note_history_records/planningNotes'),
    syncPlanningNotes: async (data: Schemas['BulkPlanningNotesRequest']) =>
        await baseClient.PUT('/planning_note_history_records/planningNotes', { body: data }),
    captureRawDipsPlanningNoteObservations: async (data: Schemas['CaptureRawDipsPlanningNoteObservationsRequest'], signal?: AbortSignal) =>
        await baseClient.POST('/raw-dips-planning-note-observations/capture', { body: data, signal }),
    deleteAllPlanningNoteHistoryRecords: async () => await baseClient.DELETE('/planning_note_history_records/planningNotes'),

    // Planning Note Suggestions Operations
    getSuggestions: async (folderId: string) => await baseClient.GET('/suggestions/{folder_id}', { params: { path: { folder_id: folderId } } }),
    fetchLatestSurgeryMetadataSuggestions: async (data: Schemas['SuggestionsLookupRequest']) => await baseClient.POST('/suggestions/latest', { body: data }),
    triggerSuggestionsAnalysis: async () => await baseClient.POST('/suggestions/trigger-analysis'),
    lookupSuggestions: async (data: operations['lookup_suggestions_suggestions_lookup_post']['requestBody']['content']['application/json']) =>
        await baseClient.POST('/suggestions/lookup', { body: data }),
    markSuggestionsAsReviewed: async (folderId: string) =>
        await baseClient.POST('/suggestions/{folder_id}/mark-reviewed', { params: { path: { folder_id: folderId } } }),
    deleteAllSuggestions: async () => await baseClient.DELETE('/suggestions'),

    // Prioritized Patient Group Operations
    createPrioritizedPatientGroup: async (data: Schemas['PrioritizedPatientGroupCreate']) =>
        await baseClient.PUT('/prioritized_patient_groups/', { body: data }),
    deletePrioritizedPatientGroup: async (prioritized_patient_group_uuid: string) =>
        await baseClient.DELETE('/prioritized_patient_groups/{prioritized_patient_group_uuid}', {
            params: { path: { prioritized_patient_group_uuid } },
        }),
    updatePrioritizedPatientGroup: async (prioritized_patient_group_uuid: string, data: Schemas['PrioritizedPatientGroupUpdate']) =>
        await baseClient.PATCH('/prioritized_patient_groups/{prioritized_patient_group_uuid}', {
            params: { path: { prioritized_patient_group_uuid } },
            body: data,
        }),

    planningPeriods: {
        createEnriched: async (data: Schemas['PlanningPeriodCreateEnriched']) => await baseClient.POST('/planning_periods/enriched/', { body: data }),
        patchEnriched: async (planning_period_id: number, data: Schemas['PlanningPeriodUpdateEnriched']) =>
            await baseClient.PATCH('/planning_periods/enriched/{planning_period_id}', {
                params: { path: { planning_period_id } },
                body: data,
            }),
    },

    viewOptionProfiles: {
        list: async () => await baseClient.GET('/view-option-profiles'),
        create: async (data: Schemas['ViewOptionProfileCreate']) => await baseClient.PUT('/view-option-profiles', { body: data }),
        update: async (id: number, data: Schemas['ViewOptionProfileUpdate']) =>
            await baseClient.PATCH('/view-option-profiles/{id}', { params: { path: { id } }, body: data }),
        select: async (id: number) => await baseClient.POST('/view-option-profiles/{id}/select', { params: { path: { id } } }),
        delete: async (id: number) => await baseClient.DELETE('/view-option-profiles/{id}', { params: { path: { id } } }),
        reset: async (page_type: string) => await baseClient.POST('/view-option-profiles/reset-selection', { body: { page_type } }),
    },

    surgeryBookmarks: {
        get: async () => await baseClient.GET('/surgery-bookmarks', {}),
        create: async (folderId: string) => await baseClient.POST('/surgery-bookmarks/{folder_id}', { params: { path: { folder_id: folderId } } }),
        delete: async (folderId: string) => await baseClient.DELETE('/surgery-bookmarks/{folder_id}', { params: { path: { folder_id: folderId } } }),
    },
} as const

export default client
